import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { DateInput, FormCheck, NumberInput } from "../../../components/forms";
import RangeInput from "../../../components/forms/RangeInput";
import { BusinessSheetStatusSelect, GenericAdviserSelect, GenericUserSelect, MasterAccountSelect, ReportStatusSelect, ReportTypesSelect, ServiceAgreementsSelect } from "../../../components/forms/selects";
import ThemedModal from "../../../components/utilities/ThemedModal";
import { useFilterContext } from "../../../hooks/FilterContext";

const AdviceRegisterFiltersModal = () => {
    const {
        filter,
        setFilter,
        showFilterModal,
        setShowFilterModal,
        defaultFilters
    } = useFilterContext();

    const onHide = () => setShowFilterModal(false);

    const [tempFilters, setTempFilters] = useState(filter);

    const updateFilter = (prop, value) => setTempFilters(old => ({
        ...old,
        [prop]: value
    }));

    const onClear = () => setTempFilters(defaultFilters);

    const reset = () => setTempFilters(filter);
    const onCancel = () => {
        reset();
        onHide();
    }

    const onApply = () => setFilter(tempFilters);
    const onConfirm = () => {
        onApply();
        onHide();
    }

    const {
        masterAccountIds,
        sentToClientStartDate,
        sentToClientEndDate,
        passedComplianceStartDate,
        passedComplianceEndDate,
        reportTypeIds,
        authorIds,
        adviserIds,
        complianceOfficerIds,
        minRejectCount,
        maxRejectCount,
        reportStatusIds,
        businessSheetStatuses,
        vulnerableOnly,
        replacementOnly,
        reviewStartDate,
        reviewEndDate,
        serviceLevelAgreementIds
    } = tempFilters;

    useEffect(() => {
        setTempFilters(filter);
    }, [filter]);

    return <Modal
        show={showFilterModal}
        onHide={onCancel}
        size="lg"
        centered
    >
        <ThemedModal.Header closeButton>
            <Modal.Title>Advice Register Filters</Modal.Title>
        </ThemedModal.Header>
        <Modal.Body>
            <div className="row gy-2">
                <div className="col-12 col-lg-6">
                    <MasterAccountSelect
                        label="Master Account"
                        value={masterAccountIds}
                        setValue={(selections) => updateFilter('masterAccountIds', selections.map(x => x.value))}
                        isClearable
                        isMulti
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <GenericAdviserSelect
                        label="Adviser"
                        defaultValue={adviserIds}
                        onChange={(selections) => updateFilter('adviserIds', selections.map(s => s.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-6">
                    <ReportTypesSelect
                        label="Report Type"
                        defaultValue={reportTypeIds}
                        onChange={(selections) => updateFilter('reportTypeIds', selections.map(s => s.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-6">
                    <ReportStatusSelect
                        label="Report Status"
                        defaultValue={reportStatusIds}
                        onChange={(selections) => updateFilter('reportStatusIds', selections.map(s => s.value))}
                        disableAnimations
                        isMulti
                        isClearable
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <GenericUserSelect
                        label="Author"
                        defaultValue={authorIds}
                        onChange={(selections) => updateFilter('authorIds', selections.map(s => s.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-12 col-lg-6 order-lg-1">
                    <GenericUserSelect
                        label="Compliance Officer"
                        defaultValue={complianceOfficerIds}
                        onChange={(selections) => updateFilter('complianceOfficerIds', selections.map(s => s.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-12 col-lg-6 order-lg-0">
                    <RangeInput
                        InputComponent={DateInput}
                        label={"Compliant"}
                        startProps={{
                            value: passedComplianceStartDate,
                            onBlur: (date) => updateFilter('passedComplianceStartDate', date)
                        }}
                        endProps={{
                            value: passedComplianceEndDate,
                            onBlur: (date) => updateFilter('passedComplianceEndDate', date)
                        }}
                        maxDate={new Date()}
                        disableAnimations
                        isClearable
                    />
                </div>
                <div className="col-12 col-lg-6 order-lg-1">
                    <RangeInput
                        InputComponent={DateInput}
                        label={"Sent"}
                        startProps={{
                            value: sentToClientStartDate,
                            onBlur: (date) => updateFilter('sentToClientStartDate', date)
                        }}
                        endProps={{
                            value: sentToClientEndDate,
                            onBlur: (date) => updateFilter('sentToClientEndDate', date)
                        }}
                        maxDate={new Date()}
                        disableAnimations
                        isClearable
                    />
                </div>
                <div className="col-6 order-lg-1">
                    <BusinessSheetStatusSelect
                        headOfficeOnly
                        label="Business Sheet Status"
                        defaultValue={businessSheetStatuses}
                        onChange={(selections) => updateFilter('businessSheetStatuses', selections.map(s => s.value))}
                        disableAnimations
                        isMulti
                        isClearable
                    />
                </div>
                <div className="col-6 order-lg-1 d-flex flex-row">
                    <RangeInput 
                        InputComponent={NumberInput}
                        label={"Reject Count"}
                        startProps={{
                            value: minRejectCount ?? "",
                            onChange: (_, { floatValue }) => updateFilter('minRejectCount', floatValue)
                        }}
                        endProps={{
                            value: maxRejectCount ?? "",
                            onChange: (_, { floatValue }) => updateFilter('maxRejectCount', floatValue)
                        }}
                        decimalScale={0}
                        disableAnimations
                    />
                </div>
                <div className="col-12 col-lg-6 order-lg-1">
                    <ServiceAgreementsSelect
                        label="Service Level"
                        defaultValue={serviceLevelAgreementIds}
                        includeNoneOption
                        onChange={(selections) => updateFilter('serviceLevelAgreementIds', selections.map(selection => selection.value))}
                        isMulti
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-12 col-lg-6 order-lg-1">
                    <RangeInput
                        InputComponent={DateInput}
                        label="Review Date"
                        startProps={{
                            value: reviewStartDate,
                            onBlur: (date) => updateFilter('reviewStartDate', date)
                        }}
                        endProps={{
                            value: reviewEndDate,
                            onBlur: (date) => updateFilter('reviewEndDate', date)
                        }}
                        isClearable
                        disableAnimations
                    />
                </div>
                <div className="col-6 order-lg-1 mt-3">
                    <FormCheck
                        label="Vulnerable Clients Only"
                        isChecked={vulnerableOnly}
                        onChange={(isChecked) => updateFilter('vulnerableOnly', isChecked)}
                        disableAnimations
                    />
                </div>
                <div className="col-6 order-lg-1 mt-3">
                    <FormCheck
                        label="Replacement Only"
                        isChecked={replacementOnly}
                        onChange={(isChecked) => updateFilter('replacementOnly', isChecked)}
                        disableAnimations
                    />
                </div>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="warning" onClick={onClear}>
                Clear Filters
            </Button>
            <Button variant="success" onClick={onConfirm}>
                Apply Filters
            </Button>
            <Button variant="light" onClick={onCancel}>
                Cancel
            </Button>
        </Modal.Footer>
    </Modal>
}

export default AdviceRegisterFiltersModal;